// Slowed Pro's billing details as the popup's Billing sheet shows them:
// what was bought, for how much, when, on which card, and Stripe's receipt.
// The background reads them from Payhook for this browser's payment hook
// (payhook/index.js getBilling); slowed-studio's src/access/billing.js
// draws the same purchase on Studio's PRO mark.

const ZERO_DECIMAL = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga",
  "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf",
]);

const METHODS = {
  pix: "Pix",
  boleto: "Boleto",
  link: "Link",
  paypal: "PayPal",
  cashapp: "Cash App Pay",
  amazon_pay: "Amazon Pay",
  revolut_pay: "Revolut Pay",
};

const BRANDS = {
  amex: "American Express",
  diners: "Diners Club",
  discover: "Discover",
  eftpos_au: "eftpos",
  elo: "Elo",
  jcb: "JCB",
  mastercard: "Mastercard",
  unionpay: "UnionPay",
  visa: "Visa",
};

// Billing by email on Payhook's hosted page: Stripe's Customer Portal, with
// the receipts and invoices, for any buyer (the hook's own manage_plan only
// opens it for a subscription).
export function billingPageUrl({ accountId, email } = {}) {
  const url = new URL("https://unlock.payhook.link/manage/");
  if (accountId) url.searchParams.set("accountId", accountId);
  if (email) url.searchParams.set("email", email);
  return url.href;
}

export function formatAmount(amount, currency, locale) {
  if (!Number.isFinite(amount) || !currency) return null;
  const code = String(currency).toLowerCase();
  const value = ZERO_DECIMAL.has(code) ? amount : amount / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code.toUpperCase(),
    }).format(value);
  } catch (_) {
    return `${value} ${code.toUpperCase()}`;
  }
}

export function paymentMethodLabel(payment) {
  if (payment?.card?.last4) {
    return `${BRANDS[payment.card.brand] || "Card"} •••• ${payment.card.last4}`;
  }
  return METHODS[payment?.paymentMethod] || null;
}

// The sheet's words for the purchase Pro stands on (the newest active one,
// else the newest), each line only when Stripe has it. Null with nothing
// to show: the sheet then says Pro is on and offers billing by email.
export function billingView(payments, { locale } = {}) {
  if (!Array.isArray(payments) || !payments.length) return null;
  const payment =
    payments.find((entry) => entry?.status === "active") || payments[0];
  if (!payment) return null;
  const lifetime = payment.mode === "payment";
  const paidAt = payment.paidAt ? new Date(payment.paidAt) : null;
  const rows = [
    ["Paid", formatAmount(payment.amount, payment.currency, locale)],
    [
      "Date",
      paidAt && !Number.isNaN(paidAt.getTime())
        ? paidAt.toLocaleDateString(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : null,
    ],
    ["Payment", paymentMethodLabel(payment)],
    ["Email", payment.email || null],
  ].filter(([, value]) => value);
  let note = lifetime
    ? "Paid once. Pro is yours for good, with every update."
    : "A subscription. Change or cancel it in Billing.";
  if (payment.status === "refunded") note = "This purchase was refunded.";
  if (payment.status === "disputed") {
    note = "This purchase is disputed with the bank.";
  }
  return {
    lifetime,
    kind: lifetime ? "Lifetime" : "Subscription",
    note,
    rows,
    email: payment.email || null,
    receiptUrl:
      typeof payment.receiptUrl === "string" &&
      /^https:\/\//.test(payment.receiptUrl)
        ? payment.receiptUrl
        : null,
  };
}
